    import React, { useEffect, useState } from "react";
import { X, FileText, Type, AlignLeft, AlertCircle, Trash2, ShieldAlert, Upload } from "lucide-react";
import { updateSurvey, deleteSurvey } from "../Services/SurveyService";
import RichTextEditor from "../../../Utility/RichTextEditor"; // match CreateSurveyModal's relative depth
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";

const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// TODO: same helper now lives in 3 files (AddQuestions, CreateSurveyModal,
// here) — extract to a shared utils module.
const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").trim();
};

const EditSurveyModal = ({ isOpen, survey, onClose, onSuccess }) => {
    const [surveyName, setSurveyName] = useState("");
    const [description, setDescription] = useState("");

    // Image & File states
    const [existingImage, setExistingImage] = useState("");
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [errors, setErrors] = useState({ surveyName: "", description: "" });
    const [submitting, setSubmitting] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    // Populate fields and existing thumbnail from the survey passed in whenever the drawer opens
    useEffect(() => {
        if (isOpen && survey) {
            setSurveyName(survey.surveyName || "");
            setDescription(survey.surveyDescription || "");
            setExistingImage(survey.thumbnailImageURL || "");
            setFile(null);
            setPreviewUrl(null);
            setErrors({ surveyName: "", description: "" });
            setConfirmingDelete(false);
        }
    }, [isOpen, survey]);

    // Handle selecting a new file
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            if (selectedFile.type.startsWith("image/")) {
                setPreviewUrl(URL.createObjectURL(selectedFile));
            } else {
                setPreviewUrl(null);
            }
        }
    };

    // Remove selected new file
    const handleRemoveNewFile = () => {
        setFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    };

    const handleSave = async () => {
        const plainDescription = stripHtml(description);

        const nextErrors = {
            surveyName: surveyName.trim() ? "" : "Assessment name is required",
            description: plainDescription ? "" : "Assessment description is required",
        };

        if (nextErrors.surveyName || nextErrors.description) {
            setErrors(nextErrors);
            return;
        }

        try {
            setSubmitting(true);
            // Pass file to your update service call
            const response = await updateSurvey(survey.uniqueKey, surveyName, description, file);
            if (response.data.status.toLowerCase() === "success") {
                CustomToast("success", response.data.message);
                onClose();
                if (onSuccess) onSuccess();
            } else {
                CustomToast("fail", response.data.message);
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const response = await deleteSurvey(survey.uniqueKey);
            if (response.data.status.toLowerCase() === "success") {
                CustomToast("success", response.data.message || "Assessment deleted.");
                onClose();
                if (onSuccess) onSuccess();
            } else {
                CustomToast("fail", response.data.message);
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "Unable to delete assessment.");
        } finally {
            setDeleting(false);
            setConfirmingDelete(false);
        }
    };

    return (
        <>
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]
        transition-opacity duration-300 z-[9998]
        ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            <div
                className={`fixed top-0 right-0 h-full w-full md:w-[790px]
        bg-white shadow-2xl z-[9999]
        rounded-l-2xl overflow-hidden
        transform transition-transform duration-300 ease-in-out
        flex flex-col
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                        style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                    />
                    <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                <FileText className="text-[#ffffff]" size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-100">Edit Assessment</h2>
                                <p className="text-sm text-gray-100 mt-0.5">Update details, image or remove this assessment</p>
                            </div>
                        </div>

                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100">
                            <X size={22} />
                        </button>
                    </div>
                </div>

                <div className="relative flex-1 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
                        style={{ backgroundImage: `url(${sidebarImage})`, backgroundSize: "360px auto" }}
                    />

                    <div className="relative h-full overflow-y-auto p-6">
                        <div className="space-y-6">
                            {/* Assessment Name */}
                            <div>
                                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                                    <Type size={14} className="text-[#7f35b2]" />
                                    Assessment Name
                                    <span className="text-red-500">*</span>
                                </label>

                                <input
                                    type="text"
                                    value={surveyName}
                                    onChange={(e) => {
                                        setSurveyName(e.target.value);
                                        if (errors.surveyName) setErrors((prev) => ({ ...prev, surveyName: "" }));
                                    }}
                                    placeholder="Enter Assessment name"
                                    className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:border-transparent outline-none transition ${errors.surveyName ? "border-red-300 focus:ring-red-200" : "border-gray-300 focus:ring-[#7f35b2]"
                                        }`}
                                />

                                {errors.surveyName && (
                                    <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                                        <AlertCircle size={12} />
                                        {errors.surveyName}
                                    </p>
                                )}
                            </div>

                            {/* Description — rich text, no table insertion */}
                            <div>
                                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                                    <AlignLeft size={14} className="text-[#7f35b2]" />
                                    Description
                                    <span className="text-red-500">*</span>
                                </label>

                                <div className={errors.description ? "rounded-xl ring-2 ring-red-200" : ""}>
                                    <RichTextEditor
                                        key={`${survey?.uniqueKey ?? "new"}-${isOpen}`}
                                        initialContent={survey?.surveyDescription || ""}
                                        placeholder="Enter Assessment description"
                                        minHeight={120}
                                        enableTable={false}
                                        onChange={(html) => {
                                            setDescription(html);
                                            if (errors.description) setErrors((prev) => ({ ...prev, description: "" }));
                                        }}
                                    />
                                </div>

                                {errors.description && (
                                    <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                                        <AlertCircle size={12} />
                                        {errors.description}
                                    </p>
                                )}
                            </div>

                            {/* Thumbnail / Image Upload & Preview Section — unchanged */}
                            <div>
                                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                                    <Upload size={14} className="text-[#7f35b2]" />
                                    Assessment Thumbnail
                                    <span className="text-gray-400 font-normal">(optional)</span>
                                </label>

                                {file ? (
                                    <div className="bg-[#c2a8f01a] border border-[#c2a8f04d] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-3 min-w-0">
                                            {previewUrl ? (
                                                <img
                                                    src={previewUrl}
                                                    alt="New Preview"
                                                    className="w-16 h-16 object-cover rounded-xl border border-[#c2a8f066] shrink-0 bg-white"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-xl bg-[#c2a8f033] flex items-center justify-center shrink-0">
                                                    <FileText className="text-[#7f35b2]" size={20} />
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <p className="text-xs font-semibold text-[#48086f] uppercase tracking-wider mb-0.5">New Image Selected</p>
                                                <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleRemoveNewFile}
                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition shrink-0"
                                        >
                                            <X size={14} /> Remove New Image
                                        </button>
                                    </div>
                                ) : existingImage ? (
                                    <div className="bg-slate-50 border border-gray-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-4">
                                        <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-white border border-gray-200 shrink-0 flex items-center justify-center">
                                            <img
                                                src={existingImage}
                                                alt="Current Survey Thumbnail"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://via.placeholder.com/150?text=No+Image";
                                                }}
                                            />
                                        </div>
                                        <div className="flex-1 text-center sm:text-left min-w-0">
                                            <p className="text-sm font-semibold text-gray-800">Current Image</p>
                                            <p className="text-xs text-gray-400 mt-0.5 truncate">{existingImage}</p>

                                            <label className="inline-flex items-center gap-1.5 mt-2.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 cursor-pointer transition shadow-sm">
                                                <Upload size={13} className="text-[#7f35b2]" />
                                                Change Image
                                                <input
                                                    type="file"
                                                    accept="image/*,.pdf,.docx,.xlsx"
                                                    hidden
                                                    onChange={handleFileChange}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#7f35b2] hover:bg-[#7f35b2]/5 transition">
                                        <Upload className="text-[#7f35b2] mb-3" size={32} />
                                        <p className="font-semibold text-gray-800 text-sm">Click to Upload Image</p>
                                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, JPEG or GIF</p>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Footer — unchanged */}
                <div className="border-t bg-white px-6 py-4 flex items-center justify-between gap-3">
                    <button
                        onClick={() => setConfirmingDelete(true)}
                        disabled={submitting || deleting || confirmingDelete}
                        className="flex items-center gap-2 px-5 py-2.5 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Trash2 size={16} />
                        Delete
                    </button>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={submitting || deleting}
                            className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={submitting || deleting}
                            className="px-5 py-2.5 bg-[#7f35b2] hover:bg-[#3bb290] text-white rounded-xl transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {confirmingDelete && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl transform transition-transform duration-300 scale-100">
                        <div className="flex items-start gap-4">
                            <div className="bg-red-100 p-3 rounded-full shrink-0">
                                <ShieldAlert className="text-red-600" size={24} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Assessment?</h3>
                                <p className="text-sm text-gray-600 mb-6">
                                    This will permanently remove "{survey?.surveyName}" and all its data. This action cannot be undone.
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => setConfirmingDelete(false)}
                                        disabled={deleting}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition disabled:opacity-60"
                                    >
                                        {deleting ? "Deleting..." : "Yes, Delete"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditSurveyModal;

